import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import "./Analytics.css";
import { apiService } from "../../utils/api";

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topStudents, setTopStudents] = useState([]);
  const [dateRange, setDateRange] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedBatch, setSelectedBatch] = useState("");
  const [batches, setBatches] = useState([]);

  // Fetch batches for the dropdown
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const response = await apiService.getActiveTeacherBatches();
        setBatches(response.data.batches);
        if (response.data.batches.length > 0) {
          setSelectedBatch(response.data.batches[0].id);
        }
      } catch (err) {
        console.error("Error fetching batches:", err);
        setError("Failed to load batches. Please try again later.");
      }
    };

    fetchBatches();
  }, []);

  // Fetch top students data when batch or date range changes
  useEffect(() => {
    const fetchTopStudents = async () => {
      if (!selectedBatch) return;

      setLoading(true);
      try {
        const response = await apiService.getStudentPerformance(selectedBatch, dateRange);

        setTopStudents(response.data.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching top students:", err);
        setError("Failed to load student statistics. Please try again later.");
        setLoading(false);
      }
    };

    fetchTopStudents();
  }, [selectedBatch, dateRange]);

  const handleDateRangeChange = (e) => {
    setDateRange(e.target.value);
  };

  const handleBatchChange = (e) => {
    setSelectedBatch(e.target.value);
  };

  // Function to determine medal color based on rank
  const getMedalColor = (rank) => {
    switch (rank) {
      case 0: return "gold";
      case 1: return "silver";
      case 2: return "bronze";
      default: return "blue";
    }
  };

  return (
    <div className="analytics-container">
      <h1>Student Performance Analytics</h1>

      <div className="analytics-controls">
        <div className="control-group">
          <label htmlFor="batch-select">Select Batch:</label>
          <select id="batch-select" value={selectedBatch} onChange={handleBatchChange} disabled={loading || batches.length === 0}>
            {batches.length === 0 ? (
              <option value="">No batches available</option>
            ) : (
              batches.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.name}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="date-range">Date Range:</label>
          <input type="date" id="date-range" value={dateRange} onChange={handleDateRangeChange} disabled={loading} className="date-picker" />
        </div>
      </div>

      {loading ? (
        <div className="analytics-loading">
          <div className="loading-spinner"></div>
          <p>Loading statistics...</p>
        </div>
      ) : (
        <>
          <div className="top-students-container">
            <h2>Top 5 Students</h2>
            {topStudents.length > 0 ? (
              <div className="student-cards-container">
                {topStudents.map((student, index) => {
                  const score = student.averageScore?.toFixed(2) || "N/A";
                  const scorePercent = student.averageScore ? Math.min(Number(score), 100) : 0;
                  
                  return (
                    <div className="student-card" key={student.id}>
                      <div className={`rank-badge rank-${getMedalColor(index)}`}>{index + 1}</div>
                      <div className="student-info">
                        <h3 className="student-name">{student.username}</h3>
                        <div className="score-container">
                          <div className="score-label">Average Score</div>
                          <div className="score-value">{score}%</div>
                          <div className="progress-bar-container">
                            <div 
                              className="progress-bar" 
                              style={{ width: `${scorePercent}%`, backgroundColor: `var(--${getMedalColor(index)}-color)`, marginTop : 0 }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-data-message">
                <div className="no-data-icon">📊</div>
                <p>No student data available for the selected batch and period.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
